import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Container, Box, StatusIndicator, Alert } from '@cloudscape-design/components';
import { canAccessPage } from '../auth/bindleAuthorization';

/**
 * ProtectedRoute component that checks Bindle Lock authorization before rendering
 * @param {Object} props
 * @param {string} props.path - The path to check authorization for
 * @param {React.ReactElement} props.children - The component to render if authorized
 * @param {string} props.redirectTo - Where to redirect if unauthorized (default: '/dashboard')
 */
const ProtectedRoute = ({ path, children, redirectTo = '/dashboard' }) => {
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    const checkAccess = async () => {
      setLoading(true);
      try {
        const access = await canAccessPage(path);
        setHasAccess(access);
      } catch (error) {
        console.error('Error checking page access:', error);
        setHasAccess(false);
      } finally {
        setLoading(false);
      }
    };

    checkAccess();
  }, [path]);

  if (loading) {
    return (
      <Container>
        <Box textAlign="center" padding="xl">
          <StatusIndicator type="loading">
            Checking permissions...
          </StatusIndicator>
        </Box>
      </Container>
    );
  }

  if (!hasAccess) {
    return (
      <Container>
        <Alert
          statusIconAriaLabel="Error"
          type="error"
          header="Access Denied"
        >
          You do not have permission to access this page. Please contact your administrator for access.
        </Alert>
        <Navigate to={redirectTo} replace />
      </Container>
    );
  }

  return children;
};

export default ProtectedRoute;
